import * as XLSX from 'xlsx';

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const HEADER_ALIASES = {
  'employee id': 'employee_id',
  employeeid: 'employee_id',
  'employee name': 'employee_name',
  name: 'employee_name',
  salary: 'amount',
  amount: 'amount',
  'payment amount': 'amount',
};

const pad = (value) => String(value).padStart(2, '0');

export const payPeriodFromDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()} ${MONTH_NAMES[date.getMonth()]}`;
};

export const buildPayPeriodOptions = (startYear = 2024) => {
  const endYear = new Date().getFullYear() + 1;
  const periods = [];
  for (let year = endYear; year >= startYear; year -= 1) {
    for (let monthIndex = MONTH_NAMES.length - 1; monthIndex >= 0; monthIndex -= 1) {
      periods.push(`${year} ${MONTH_NAMES[monthIndex]}`);
    }
  }
  return periods;
};

export const roundMoney = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  return Math.round(amount * 100) / 100;
};

const normalizeHeader = (value) =>
  String(value || '')
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()
    .replace(/[._]/g, ' ')
    .replace(/\s+/g, ' ');

const toIsoDate = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }

  if (typeof value === 'number' && value > 20000 && value < 80000) {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (!parsed) return '';
    return `${parsed.y}-${pad(parsed.m)}-${pad(parsed.d)}`;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      return trimmed.slice(0, 10);
    }
  }

  return '';
};

const findHeaderRow = (rows) => {
  for (let index = 0; index < rows.length; index += 1) {
    const mapped = {};
    (rows[index] || []).forEach((cell, colIndex) => {
      const field = HEADER_ALIASES[normalizeHeader(cell)];
      if (field && mapped[field] === undefined) {
        mapped[field] = colIndex;
      }
    });

    if (mapped.employee_id !== undefined && mapped.amount !== undefined) {
      return { headerIndex: index, columns: mapped };
    }
  }

  return null;
};

const findFileDate = (rows, headerIndex) => {
  for (let rowIndex = 0; rowIndex < headerIndex; rowIndex += 1) {
    const row = rows[rowIndex] || [];
    for (let colIndex = 0; colIndex < row.length; colIndex += 1) {
      const isoDate = toIsoDate(row[colIndex]);
      if (isoDate) return isoDate;
    }
  }
  return '';
};

const parseEmployeeId = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const id = Number(String(value).trim());
  if (!Number.isFinite(id) || id <= 0) return null;
  const whole = Math.round(id);
  if (Math.abs(id - whole) > 0.001) return null;
  return whole;
};

export const parseSalaryImportFile = (arrayBuffer, fileName = '') => {
  const lowerName = String(fileName).toLowerCase();
  const isCsv = lowerName.endsWith('.csv');
  const workbook = isCsv
    ? XLSX.read(new TextDecoder().decode(arrayBuffer), { type: 'string' })
    : XLSX.read(arrayBuffer, { type: 'array', cellDates: false });

  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    throw new Error('The file does not contain a sheet to import.');
  }

  const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
    header: 1,
    raw: true,
    defval: '',
    blankrows: false,
  });

  const header = findHeaderRow(rows);
  if (!header) {
    throw new Error('Could not find Employee ID and Salary columns in this file.');
  }

  const { headerIndex, columns } = header;
  const parsedRows = [];
  const errors = [];

  for (let rowIndex = headerIndex + 1; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex] || [];
    const employeeId = parseEmployeeId(row[columns.employee_id]);
    const employeeName = String(row[columns.employee_name] || '').trim();
    const amount = roundMoney(row[columns.amount]);
    const lineNumber = rowIndex + 1;

    if (!employeeId) {
      if (employeeName) {
        errors.push(`Row ${lineNumber}: missing Employee ID.`);
      }
      continue;
    }

    if (amount === null || amount <= 0) {
      errors.push(`Row ${lineNumber}: salary amount must be greater than 0.`);
      continue;
    }

    parsedRows.push({
      employee_id: employeeId,
      employee_name: employeeName,
      amount,
      line_number: lineNumber,
    });
  }

  if (parsedRows.length === 0) {
    throw new Error(errors[0] || 'No salary rows were found in this file.');
  }

  const duplicateIds = parsedRows.reduce((counts, row) => {
    counts[row.employee_id] = (counts[row.employee_id] || 0) + 1;
    return counts;
  }, {});
  Object.entries(duplicateIds)
    .filter(([, count]) => count > 1)
    .forEach(([employeeId]) => {
      errors.push(`Employee ID ${employeeId} appears more than once in the file.`);
    });

  return {
    fileDate: findFileDate(rows, headerIndex),
    rows: parsedRows,
    errors,
    totalAmount: parsedRows.reduce((sum, row) => sum + row.amount, 0),
  };
};
